import * as main from './alan_api';
import * as gui_model from '../gui_model/alan_api';
import * as settings from '../settings/alan_api';
import * as views from '../views/alan_api';

type SymbolsFile = [string, SymbolsList];
type SymbolsList = Array<[string, string, string, [number, number, number], [number, number, number]]>;

const debug = false;
function isFunction(p: any): p is Function {
	return (typeof p === 'function');
}
function assert(predicate:boolean) {
	if (predicate === false) throw new Error(`Assertion error`);
}
function resolve<T>(context:T) {
	return {
		then: <RT>(callback: (context: T) => RT) => resolve(callback(context)),
		result: context
	};
}
export type dictionary_type<T> = {[key:string]:T};


enum ResolutionStatus {
	Resolved,
	Resolving,
	Unresolved,
	Detached,
}
function cache<T extends IAlanObject>(callback:(detach:boolean) => T) {
	let cached_value:T;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				(cached_value as any) = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

function maybe_cache<T extends IAlanObject>(callback:(detach:boolean) => T|undefined) {
	let cached_value:T|undefined;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				cached_value = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

/* number validation */
function number__is_positive(val:number) { assert(val > 0); }
function number__is_negative(val:number) { assert(val < 0); }
function number__is_zero(val:number) { assert(val === 0); }
function number__is_non_positive(val:number) { assert(val <= 0); }
function number__is_non_negative(val:number) { assert(val >= 0); }
function number__is_non_zero(val:number) { assert(val !== 0); }

/* complex value wrappers */
export abstract class AlanObject {
	public abstract get path(): string;
	private _destroyed?: true;
	get destroyed(): boolean {
		return this._destroyed ? true : false;
	}
}
export interface IAlanObject {
	readonly path: string;
	readonly destroyed: boolean;
};

export abstract class AlanStruct extends AlanObject {
	public abstract is(other:AlanStruct):boolean;
}
export interface Tree<T> {
	types: {[name:string]:T};
	subtrees: {[name:string]:Tree<T>};
}
export abstract class Reference<T extends IAlanObject, V extends (string|string[])> extends AlanObject {
	constructor(public readonly entry:V, private readonly resolve:() => T, protected readonly parent: AlanNode) { super(); }
	public get ref() { return this.resolve(); }

}
export abstract class AlanInteger extends AlanObject {
	constructor(public readonly value:number, protected readonly parent: AlanNode) { super(); }

}
export abstract class AlanDictionary<T extends {node: AlanDictionaryEntry, init: any }, P extends AlanNode> extends AlanObject {
	protected _entries:Map<string, () => T['node']>;

	constructor (entries:{[key:string]:T['init']}, protected parent:P) {
		super();
		this._entries = new Map(Object.keys(entries).map(entry_key => [entry_key, () => {
			const loaded_entry = this.initialize(this.parent, entry_key, entries[entry_key])
			this._entries.set(entry_key, () => loaded_entry);
			return loaded_entry;
		}]));
	}

	protected abstract initialize(parent: P, key: string, obj: T['init']): T['node'];
	protected abstract finalize(obj: T['node'], detach?: boolean): void;

	get size() { return this._entries.size; }
	[Symbol.iterator](): IterableIterator<[string, T['node']]> {
		const iterator = this._entries.entries();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: [next.value[0], next.value[1]()]
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	entries(): IterableIterator<[string, T['node']]> {
		return this[Symbol.iterator]();
	}
	values(): IterableIterator<T['node']> {
		const iterator = this._entries.values();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: next.value()
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	keys(): IterableIterator<string> {
		return this._entries.keys();
	}
	forEach(walk_function: ($:T['node']) => void) {
		for (const entry of this.values()) {
			walk_function(entry);
		}
	}
	toArray(): [string, T['node']][] {
		return Array.from(this.entries());
	}
	map<RT>(callback: (value: T['node']) => RT): Record<string, RT> {
		const result:Record<string, RT> = {};
		for (const [key, entry] of this.entries()) {
			result[key] = callback(entry);
		}
		return result;
	}
	get(key:string): T['node'] | undefined {
		return this._entries.get(key)?.();
	}
	has(key: string): boolean { return this._entries.has(key); }
	switchOnEntryExists<RT>(key: string, onExists: (($: T['node']) => RT) | Exclude<RT, Function>, onNotExists: (() => RT) | Exclude<RT, Function>): RT {
		const entry = this._entries.get(key);
		if (entry === undefined) {
			return isFunction(onNotExists) ? onNotExists() : onNotExists;
		} else {
			return isFunction(onExists) ? onExists(entry()) : onExists;
		}
	}
}

export abstract class AlanSet<T extends {node: AlanNode, init: any }, P extends AlanNode> extends AlanObject {
	private _entries:Set<T['node']>;
	constructor (
		entries:Array<T['init']>,
		protected parent:P) {
		super();
		this._entries = new Set(entries.map(entry => this.initialize(parent, entry)));
	}

	protected abstract initialize(parent:P, obj:T['init']):T['node'];
	protected abstract finalize(obj:T['node'],detach?:boolean):void;

	get size() { return this._entries.size; }
	[Symbol.iterator]() {
		return this._entries[Symbol.iterator]();
	}
	entries() {
		return this._entries.entries();
	}
	forEach(walk_function: ($:T['node']) => void) {
		this._entries.forEach(walk_function);
	}
	has(key:T['node']):boolean { return this._entries.has(key); }
}
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;
export abstract class StateGroup<T extends {name:string, node:AlanNode & {parent:AlanNode}, init:any}> extends AlanObject {
	public state: DistributiveOmit<T,'init'>;
	public readonly root:Cmain
	private init(state_name:T['name'], init:T['init'], parent:AlanNode) {
		this.state = {
			name: state_name,
			node: this.initializer(state_name)(init, parent),
		} as DistributiveOmit<T,'init'>;
	}
	constructor (s:[T['name'],T['init']]|T['name'], protected readonly parent: AlanNode, readonly name: string) {
		super();
		this.root = parent.root
		const state_name:T['name'] = (typeof s === 'string') ? s : s[0];
		this.init(state_name, typeof s === 'string' ? {} : s[1], parent);
	}

	protected abstract initializer(state_name:T['name']): ($:T['init'], parent:AlanNode) => T['node'];
	protected abstract finalizer(state:T['name']): ($:T['node'], detach?:boolean) => void;

	switch<TS> (cases:{[K in T['name']]:(($:Extract<T, {name:K}>['node']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const handler = cases[this.state.name as T['name']];
		if (isFunction(handler)) {
			return handler(this.state.node);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	cast<S extends T['name']>(state:S):Extract<T, {name:S}>['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.state.name === state) {
			return this.state.node;
		} else {
			throw new Error(`Invalid cast to state '${state}'; actual state is '${this.state.name}' at ${this.state.node.path} .`);
		}
	}
}

export abstract class AlanCombinator extends AlanObject {}
export abstract class AlanNode extends AlanStruct {
	protected _root:Cmain|undefined;
	public abstract get root():Cmain;
	public abstract get entity():AlanNode;
	public is(other:AlanNode):boolean {
		return this === other;
	}
}

export abstract class AlanDictionaryEntry extends AlanNode {
	public abstract key:(string|Reference<IAlanObject,string>);
	public abstract get key_value():string;
};
export abstract class AlanTopology<T extends { node:AlanDictionaryEntry, init:any }, P extends AlanNode, G extends string> extends AlanDictionary<T,P> {
	protected abstract graph_iterator(key:string):(entry:T['node']) => T['node'];
	private findFirst(iterator:(entry:T['node']) => T['node']) {
		const key_of = (entry:T['node']) => typeof entry.key === 'string' ? entry.key : entry.key.entry;
		let done:Record<string, boolean> = {};
		let first:T['node']|undefined = undefined;
		for (let [entry_id, current] of this) {
			if (!done[entry_id]) {
				first = current;
				while (current && !done[entry_id]) {
					done[entry_id] = true;
					current = iterator(current);
					if (current !== undefined) {
						entry_id = key_of(current);
					}
				}
			}
		}
		return first;
	}
	topo_entries(graph:G, first?:T['node']):IterableIterator<T['node']> {
		let iterator = this.graph_iterator(graph);
		let current = first || this.findFirst(iterator);
		return {
			next() {
				if (current !== undefined) {
					const entry = current;
					current = iterator(current);
					return {
						value: entry
					};
				} else {
					return {
						done: true,
						value: undefined
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	topo_forEach(graph:G, walk_function: ($:T['node']) => void, first?:T['node']) {
		for (let entry of this.topo_entries(graph, first)) { walk_function(entry); }
	}
	topo_toArray(graph:G, first?:T['node']):T['node'][] {
		return Array.from(this.topo_entries(graph, first));
	}
}

/* alan objects */
export type Tview_expression_list = {
	'expression':Tview_expression;
	'has alternative':'no'|['no', {}]|['yes', Tyes__has_alternative];
};
export class Cview_expression_list extends AlanNode {
	public readonly properties:{
		readonly expression:Cview_expression,
		readonly has_alternative:Cview_expression_list.Dhas_alternative
	};
	constructor(init:Tview_expression_list, public location:AlanNode, public input: {
		node: () => gui_model.Cgui_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cview_expression_list.Dexpression(init['expression'], $this),
			has_alternative: new Cview_expression_list.Dhas_alternative(init['has alternative'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_alternative = {
};
export class Cno__has_alternative extends AlanNode {
	constructor(init:Tno__has_alternative, public parent:Cview_expression_list) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has alternative'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_alternative = {
	'tail':Tview_expression_list;
};
export class Cyes__has_alternative extends AlanNode {
	public readonly properties:{
		readonly tail:Cview_expression_list
	};
	constructor(init:Tyes__has_alternative, public parent:Cview_expression_list) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_alternative.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has alternative'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tview_expression = {
	'type':['branches', Tbranches]|['collection lookup', Tcollection_lookup]|['group step', Tgroup_step]|'none'|['none', {}]|['reference step', Treference_step]|['state switch', Tstate_switch]|['view open', Tview_open];
};
export class Cview_expression extends AlanNode {
	public readonly properties:{
		readonly type:Cview_expression.Dtype
	};
	constructor(init:Tview_expression, public location:AlanNode, public input: {
		node: () => gui_model.Cgui_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cview_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tbranches = {
	'list':Tview_expression_list;
};
export class Cbranches extends AlanNode {
	public readonly properties:{
		readonly list:Cview_expression_list
	};
	constructor(init:Tbranches, public parent:Cview_expression) {
		super();
		const $this = this;
		this.properties = {
			list: new Cbranches.Dlist(init['list'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'branches'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_lookup = {
	'collection':string;
	'expression':Tview_expression;
	'references users':Tnode_equality;
};
export class Ccollection_lookup extends AlanNode {
	public readonly properties:{
		readonly collection:Ccollection_lookup.Dcollection,
		readonly expression:Cview_expression,
		readonly references_users:Cnode_equality & { readonly inferences: {
			constraint: () => gui_model.Cgui_node;
		} }
	};
	public readonly inferences:{
		base: () => gui_model.Cyes__has_user_node
	} = {
		base: cache((detach:boolean) => {
			const main__view_expression__type__collection_lookup_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.gui_model)
				.then(context => {
					if (context?.properties.has_user_node.state.name === 'yes') {
						return resolve(context.properties.has_user_node.state.node as gui_model.Cyes__has_user_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection_lookup, public parent:Cview_expression) {
		super();
		const $this = this;
		this.properties = {
			collection: new Ccollection_lookup.Dcollection(init['collection'], $this),
			expression: new Ccollection_lookup.Dexpression(init['expression'], $this),
			references_users: new Ccollection_lookup.Dreferences_users(init['references users'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection lookup'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup_step = {
	'expression':Tview_expression;
	'group':string;
};
export class Cgroup_step extends AlanNode {
	public readonly properties:{
		readonly expression:Cview_expression,
		readonly group:Cgroup_step.Dgroup
	};
	constructor(init:Tgroup_step, public parent:Cview_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cgroup_step.Dexpression(init['expression'], $this),
			group: new Cgroup_step.Dgroup(init['group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group step'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone = {
};
export class Cnone extends AlanNode {
	constructor(init:Tnone, public parent:Cview_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference_step = {
	'expression':Tview_expression;
	'type':['context rule', Tcontext_rule]|['property rule', Tproperty_rule]|['reference', Treference];
};
export class Creference_step extends AlanNode {
	public readonly properties:{
		readonly expression:Cview_expression,
		readonly type:Creference_step.Dtype
	};
	constructor(init:Treference_step, public parent:Cview_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Creference_step.Dexpression(init['expression'], $this),
			type: new Creference_step.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference step'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcontext_rule = {
	'rule':string;
};
export class Ccontext_rule extends AlanNode {
	public readonly properties:{
		readonly rule:Ccontext_rule.Drule
	};
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.properties.referenced_node)
			.then(context => context?.component_root.output.result_gui_node()).result!)
	}
	public readonly inferences:{
		state: () => gui_model.Cstates__state_group__type__property__type__attributes
	} = {
		state: cache((detach:boolean) => {
			const main__view_expression__type__reference_step__type__context_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.node())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'state' ? context.variant.definition as gui_model.Cstates__state_group__type__property__type__attributes : undefined).result!;
		})

	}
	constructor(init:Tcontext_rule, public parent:Creference_step) {
		super();
		const $this = this;
		this.properties = {
			rule: new Ccontext_rule.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'context rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty_rule = {
	'property':string;
	'rule':string;
};
export class Cproperty_rule extends AlanNode {
	public readonly properties:{
		readonly property:Cproperty_rule.Dproperty,
		readonly rule:Cproperty_rule.Drule
	};
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.properties.referenced_node)
			.then(context => context?.component_root.output.result_gui_node()).result!)
	}
	constructor(init:Tproperty_rule, public parent:Creference_step) {
		super();
		const $this = this;
		this.properties = {
			property: new Cproperty_rule.Dproperty(init['property'], $this),
			rule: new Cproperty_rule.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference = {
	'reference':string;
};
export class Creference extends AlanNode {
	public readonly properties:{
		readonly reference:Creference.Dreference
	};
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.reference?.ref)
			.then(context => context?.properties.referenced_node)
			.then(context => context?.component_root.output.result_gui_node()).result!)
	}
	constructor(init:Treference, public parent:Creference_step) {
		super();
		const $this = this;
		this.properties = {
			reference: new Creference.Dreference(init['reference'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_switch = {
	'cases':Record<string, Tcases>;
	'stategroup':string;
};
export class Cstate_switch extends AlanNode {
	public readonly properties:{
		readonly cases:Cstate_switch.Dcases,
		readonly stategroup:Cstate_switch.Dstategroup
	};
	constructor(init:Tstate_switch, public parent:Cview_expression) {
		super();
		const $this = this;
		this.properties = {
			cases: new Cstate_switch.Dcases(init['cases'], $this),
			stategroup: new Cstate_switch.Dstategroup(init['stategroup'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state switch'`; }
	public get entity() { return this.parent.entity; }
}
export class Kcases extends Reference<gui_model.Cstates__state_group__type__property__type__attributes, string> {
	constructor(key:string, $this:Ccases) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.properties.stategroup?.ref)
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'::key`; }
}
export type Tcases = {
	'expression':Tview_expression;
	'path':Tancestor_node_path;
};
export class Ccases extends AlanDictionaryEntry {
	public key:Kcases;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly expression:Cview_expression,
		readonly path:Cancestor_node_path
	};
	constructor(key:string, init:Tcases, public parent:Cstate_switch) {
		super();
		const $this = this;
		this.key = new Kcases(key, $this);
		this.properties = {
			expression: new Ccases.Dexpression(init['expression'], $this),
			path: new Ccases.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tview_open = {
	'from':string;
	'node':['current', Tcurrent]|'root'|['root', {}]|'user'|['user', {}];
	'view':string;
};
export class Cview_open extends AlanNode {
	public readonly properties:{
		readonly from:Cview_open.Dfrom,
		readonly node:Cview_open.Dnode,
		readonly view:Cview_open.Dview
	};
	constructor(init:Tview_open, public parent:Cview_expression) {
		super();
		const $this = this;
		this.properties = {
			from: new Cview_open.Dfrom(init['from'], $this),
			node: new Cview_open.Dnode(init['node'], $this),
			view: new Cview_open.Dview(init['view'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'view open'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcurrent = {
	'path':Tancestor_node_path;
};
export class Ccurrent extends AlanNode {
	public readonly properties:{
		readonly path:Cancestor_node_path
	};
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.node()).result!)
	}
	constructor(init:Tcurrent, public parent:Cview_open) {
		super();
		const $this = this;
		this.properties = {
			path: new Ccurrent.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'node'?'current'`; }
	public get entity() { return this.parent.entity; }
}
export type Troot__node = {
};
export class Croot__node extends AlanNode {
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.root.input.gui_model)
			.then(context => context?.properties.root).result!)
	}
	constructor(init:Troot__node, public parent:Cview_open) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'node'?'root'`; }
	public get entity() { return this.parent.entity; }
}
export type Tuser__node = {
};
export class Cuser__node extends AlanNode {
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.base())
			.then(context => context?.properties.users?.ref)
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		base: () => gui_model.Cyes__has_user_node
	} = {
		base: cache((detach:boolean) => {
			const main__view_expression__type__view_open__node__user_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.gui_model)
				.then(context => {
					if (context?.properties.has_user_node.state.name === 'yes') {
						return resolve(context.properties.has_user_node.state.node as gui_model.Cyes__has_user_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tuser__node, public parent:Cview_open) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'node'?'user'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_equality = {
};
export class Cnode_equality extends AlanNode {
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node()).result!)
	};
	constructor(init:Tnode_equality, public location:AlanNode, public input: {
		node: () => gui_model.Cgui_node
	}, private key: string) {
		super();
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tancestor_node_path = {
	'has step':'no'|['no', {}]|['yes', Tyes__has_step];
};
export class Cancestor_node_path extends AlanNode {
	public readonly properties:{
		readonly has_step:Cancestor_node_path.Dhas_step
	};
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_step?.state.node.output.node()).result!)
	};
	constructor(init:Tancestor_node_path, public location:AlanNode, public input: {
		node: () => gui_model.Cgui_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_step: new Cancestor_node_path.Dhas_step(init['has step'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_step = {
};
export class Cno__has_step extends AlanNode {
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node()).result!)
	}
	constructor(init:Tno__has_step, public parent:Cancestor_node_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has step'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_step = {
	'tail':Tancestor_node_path;
};
export class Cyes__has_step extends AlanNode {
	public readonly properties:{
		readonly tail:Cancestor_node_path
	};
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.node()).result!)
	}
	public readonly inferences:{
		parent: () => gui_model.Cgui_node
	} = {
		parent: cache((detach:boolean) => {
			const main__ancestor_node_path__has_step__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.node())
				.then(context => context?.component_root.output.member())
				.then(context => context?.component_root.output.parent())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})

	}
	constructor(init:Tyes__has_step, public parent:Cancestor_node_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_step.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has step'?'yes'`; }
	public get entity() { return this.parent.entity; }
}

export type Tmain = {
	'initial content':Record<string, Tinitial_content>;
	'root view':string;
	'root window':string;
	'views':string;
};
export class Cmain extends AlanNode {
	public key?:string;
	public get root() { return this; }
	public readonly properties:{
		readonly initial_content:Cmain.Dinitial_content,
		readonly root_view:Cmain.Droot_view,
		readonly root_window:Cmain.Droot_window,
		readonly views:Cmain.Dviews
	};
	constructor(init:Tmain, public readonly input: {
	'gui_model':gui_model.Cgui_model
	'settings':settings.Csettings
	'views':{[name:string]:views.Cviews}}, symbols_file?: SymbolsFile) {
		super();
		const $this = this;
		this.properties = {
			initial_content: new Cmain.Dinitial_content(init['initial content'], $this),
			root_view: new Cmain.Droot_view(init['root view'], $this),
			root_window: new Cmain.Droot_window(init['root window'], $this),
			views: new Cmain.Dviews(init['views'], $this)
		};
	}
	public get path() { return ``; }
	public get entity() { return this; }
}
export class Kinitial_content extends Reference<settings.Cwindows, string> {
	constructor(key:string, $this:Cinitial_content) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.root.input.settings)
			.then(context => {
				const entry = context?.properties.windows.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'initial content'::key`; }
}
export type Tinitial_content = {
	'expression':Tview_expression;
	'from':'root'|['root', {}]|'user'|['user', {}];
};
export class Cinitial_content extends AlanDictionaryEntry {
	public key:Kinitial_content;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly expression:Cview_expression,
		readonly from:Cinitial_content.Dfrom
	};
	constructor(key:string, init:Tinitial_content, public parent:Cmain) {
		super();
		const $this = this;
		this.key = new Kinitial_content(key, $this);
		this.properties = {
			expression: new Cinitial_content.Dexpression(init['expression'], $this),
			from: new Cinitial_content.Dfrom(init['from'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'initial content'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Troot__from = {
};
export class Croot__from extends AlanNode {
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.root.input.gui_model)
			.then(context => context?.properties.root).result!)
	}
	constructor(init:Troot__from, public parent:Cinitial_content) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'from'?'root'`; }
	public get entity() { return this.parent.entity; }
}
export type Tuser__from = {
};
export class Cuser__from extends AlanNode {
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.base())
			.then(context => context?.properties.users?.ref)
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		base: () => gui_model.Cyes__has_user_node
	} = {
		base: cache((detach:boolean) => {
			const main__initial_content__from__user_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.gui_model)
				.then(context => {
					if (context?.properties.has_user_node.state.name === 'yes') {
						return resolve(context.properties.has_user_node.state.node as gui_model.Cyes__has_user_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tuser__from, public parent:Cinitial_content) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'from'?'user'`; }
	public get entity() { return this.parent.entity; }
}

/* property classes */
export namespace Cancestor_node_path {
	export class Dhas_step extends StateGroup<
		{ name: 'no', node:Cno__has_step, init:Tno__has_step}|
		{ name: 'yes', node:Cyes__has_step, init:Tyes__has_step}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_step, parent:Cancestor_node_path) => new Cno__has_step(init, parent);
				case 'yes': return (init:Tyes__has_step, parent:Cancestor_node_path) => new Cyes__has_step(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_step;
				case 'yes': return finalize_yes__has_step;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tancestor_node_path['has step'], parent:Cancestor_node_path) {
			super(data, parent, 'has step');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has step'`; }
	}
}
export namespace Cyes__has_step {
	export class Dtail extends Cancestor_node_path {
		constructor(data:Tyes__has_step['tail'], parent:Cyes__has_step) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.parent()).result!)
			}, 'tail')
		}
	}
}
export namespace Cview_expression {
	export class Dtype extends StateGroup<
		{ name: 'branches', node:Cbranches, init:Tbranches}|
		{ name: 'collection lookup', node:Ccollection_lookup, init:Tcollection_lookup}|
		{ name: 'group step', node:Cgroup_step, init:Tgroup_step}|
		{ name: 'none', node:Cnone, init:Tnone}|
		{ name: 'reference step', node:Creference_step, init:Treference_step}|
		{ name: 'state switch', node:Cstate_switch, init:Tstate_switch}|
		{ name: 'view open', node:Cview_open, init:Tview_open}> {
		protected initializer(state: 'branches'|'collection lookup'|'group step'|'none'|'reference step'|'state switch'|'view open') {
			switch (state) {
				case 'branches': return (init:Tbranches, parent:Cview_expression) => new Cbranches(init, parent);
				case 'collection lookup': return (init:Tcollection_lookup, parent:Cview_expression) => new Ccollection_lookup(init, parent);
				case 'group step': return (init:Tgroup_step, parent:Cview_expression) => new Cgroup_step(init, parent);
				case 'none': return (init:Tnone, parent:Cview_expression) => new Cnone(init, parent);
				case 'reference step': return (init:Treference_step, parent:Cview_expression) => new Creference_step(init, parent);
				case 'state switch': return (init:Tstate_switch, parent:Cview_expression) => new Cstate_switch(init, parent);
				case 'view open': return (init:Tview_open, parent:Cview_expression) => new Cview_open(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'branches'|'collection lookup'|'group step'|'none'|'reference step'|'state switch'|'view open') {
			switch (state) {
				case 'branches': return finalize_branches;
				case 'collection lookup': return finalize_collection_lookup;
				case 'group step': return finalize_group_step;
				case 'none': return finalize_none;
				case 'reference step': return finalize_reference_step;
				case 'state switch': return finalize_state_switch;
				case 'view open': return finalize_view_open;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tview_expression['type'], parent:Cview_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cbranches {
	export class Dlist extends Cview_expression_list {
		constructor(data:Tbranches['list'], parent:Cbranches) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node()).result!)
			}, 'list')
		}
	}
}
export namespace Ccollection_lookup {
	export class Dcollection extends Reference<gui_model.Ccollection__type__property,string> {
		public readonly inferences:{
			key_navigable: () => gui_model.Cyes__navigable
		}

		constructor(data:string, $this:Ccollection_lookup) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as gui_model.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				key_navigable: cache((detach:boolean) => {
					const main__view_expression__type__collection_lookup__collection_nval = $this.properties.collection.ref;
					return resolve($this.properties.collection.ref)
						.then(context => main__view_expression__type__collection_lookup__collection_nval)
						.then(context => context?.properties.node?.inferences.text_key())
						.then(context => {
							if (context?.properties.navigable.state.name === 'yes') {
								return resolve(context.properties.navigable.state.node as gui_model.Cyes__navigable).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
	export class Dexpression extends Cview_expression {
		constructor(data:Tcollection_lookup['expression'], parent:Ccollection_lookup) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.collection?.ref)
					.then(context => context?.properties.node).result!)
			}, 'expression')
		}
	}
	export class Dreferences_users extends Cnode_equality {
		public readonly inferences:{
			constraint: () => gui_model.Cgui_node
		}
		constructor(data:Tcollection_lookup['references users'], parent:Ccollection_lookup) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.collection?.inferences.key_navigable())
					.then(context => context?.properties.referenced_node)
					.then(context => context?.component_root.output.result_gui_node())
					.then(context => context?.component_root.output.gui_entity()).result!)
			}, 'references users')
			this.inferences = {
				constraint: cache((detach:boolean) => {
					const main__view_expression__type__collection_lookup__references_users_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => main__view_expression__type__collection_lookup__references_users_nval)
								.then(context => context?.component_root.output.node()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.inferences.base())
								.then(context => context?.properties.users?.ref)
								.then(context => context?.properties.node).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Cgroup_step {
	export class Dexpression extends Cview_expression {
		constructor(data:Tgroup_step['expression'], parent:Cgroup_step) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.group?.ref)
					.then(context => context?.properties.gui_node).result!)
			}, 'expression')
		}
	}
	export class Dgroup extends Reference<gui_model.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as gui_model.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
}
export namespace Creference_step {
	export class Dexpression extends Cview_expression {
		constructor(data:Treference_step['expression'], parent:Creference_step) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.node()).result!)
			}, 'expression')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'context rule', node:Ccontext_rule, init:Tcontext_rule}|
		{ name: 'property rule', node:Cproperty_rule, init:Tproperty_rule}|
		{ name: 'reference', node:Creference, init:Treference}> {
		protected initializer(state: 'context rule'|'property rule'|'reference') {
			switch (state) {
				case 'context rule': return (init:Tcontext_rule, parent:Creference_step) => new Ccontext_rule(init, parent);
				case 'property rule': return (init:Tproperty_rule, parent:Creference_step) => new Cproperty_rule(init, parent);
				case 'reference': return (init:Treference, parent:Creference_step) => new Creference(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'context rule'|'property rule'|'reference') {
			switch (state) {
				case 'context rule': return finalize_context_rule;
				case 'property rule': return finalize_property_rule;
				case 'reference': return finalize_reference;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Treference_step['type'], parent:Creference_step) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccontext_rule {
	export class Drule extends Reference<gui_model.Crules,string> {

		constructor(data:string, $this:Ccontext_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state())
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Cproperty_rule {
	export class Dproperty extends Reference<gui_model.Cyes__navigable,string> {

		constructor(data:string, $this:Cproperty_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as gui_model.Ctext__type__property__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.navigable.state.name === 'yes') {
							return context.properties.navigable.state.node as gui_model.Cyes__navigable;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Drule extends Reference<gui_model.Crules,string> {

		constructor(data:string, $this:Cproperty_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.property?.ref)
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Creference {
	export class Dreference extends Reference<gui_model.Cyes__navigable,string> {

		constructor(data:string, $this:Creference) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as gui_model.Ctext__type__property__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.navigable.state.name === 'yes') {
							return context.properties.navigable.state.node as gui_model.Cyes__navigable;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'reference'`; }
	}
}
export namespace Cstate_switch {
	export class Dcases extends AlanDictionary<{ node:Ccases, init:Tcases},Cstate_switch> {
		protected initialize(parent:Cstate_switch, key:string, entry_init:Tcases) { return new Ccases(key, entry_init, parent); }
		protected finalize = finalize_cases
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(main__view_expression__type__state_switch__cases_key_nval:gui_model.Cstates__state_group__type__property__type__attributes):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.properties.stategroup?.ref)
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'cases'`; }
		constructor(data:Tstate_switch['cases'], parent:Cstate_switch) {
			super(data, parent);
		}
	}
	export class Dstategroup extends Reference<gui_model.Cstate_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate_switch) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'stategroup'`; }
	}
}
export namespace Ccases {
	export class Dexpression extends Cview_expression {
		constructor(data:Tcases['expression'], parent:Ccases) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.path)
					.then(context => context?.component_root.output.node()).result!)
			}, 'expression')
		}
	}
	export class Dpath extends Cancestor_node_path {
		constructor(data:Tcases['path'], parent:Ccases) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.gui_node).result!)
			}, 'path')
		}
	}
}
export namespace Cview_open {
	export class Dfrom extends Reference<views.Cviews,string> {

		constructor(data:string, $this:Cview_open) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this.root.input.views[this.entry]).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'from'`; }
	}
	export class Dnode extends StateGroup<
		{ name: 'current', node:Ccurrent, init:Tcurrent}|
		{ name: 'root', node:Croot__node, init:Troot__node}|
		{ name: 'user', node:Cuser__node, init:Tuser__node}> {
		protected initializer(state: 'current'|'root'|'user') {
			switch (state) {
				case 'current': return (init:Tcurrent, parent:Cview_open) => new Ccurrent(init, parent);
				case 'root': return (init:Troot__node, parent:Cview_open) => new Croot__node(init, parent);
				case 'user': return (init:Tuser__node, parent:Cview_open) => new Cuser__node(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'current'|'root'|'user') {
			switch (state) {
				case 'current': return finalize_current;
				case 'root': return finalize_root__node;
				case 'user': return finalize_user__node;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tview_open['node'], parent:Cview_open) {
			super(data, parent, 'node');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'node'`; }
	}
	export class Dview extends Reference<views.Cviews__,string> {
		public readonly inferences:{
			constraint: () => gui_model.Cgui_node
		}

		constructor(data:string, $this:Cview_open) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.from?.ref)
				.then(context => {
					const entry = context?.properties.views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				constraint: cache((detach:boolean) => {
					const main__view_expression__type__view_open__view_nval = $this.properties.view.ref;
					return resolve($this.properties.view.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => main__view_expression__type__view_open__view_nval)
								.then(context => context?.properties.node_type)
								.then(context => context?.component_root.output.result_gui_node()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.properties.node?.state.node.output.node()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'view'`; }
	}
}
export namespace Ccurrent {
	export class Dpath extends Cancestor_node_path {
		constructor(data:Tcurrent['path'], parent:Ccurrent) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node()).result!)
			}, 'path')
		}
	}
}
export namespace Cview_expression_list {
	export class Dexpression extends Cview_expression {
		constructor(data:Tview_expression_list['expression'], parent:Cview_expression_list) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node()).result!)
			}, 'expression')
		}
	}
	export class Dhas_alternative extends StateGroup<
		{ name: 'no', node:Cno__has_alternative, init:Tno__has_alternative}|
		{ name: 'yes', node:Cyes__has_alternative, init:Tyes__has_alternative}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_alternative, parent:Cview_expression_list) => new Cno__has_alternative(init, parent);
				case 'yes': return (init:Tyes__has_alternative, parent:Cview_expression_list) => new Cyes__has_alternative(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_alternative;
				case 'yes': return finalize_yes__has_alternative;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tview_expression_list['has alternative'], parent:Cview_expression_list) {
			super(data, parent, 'has alternative');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has alternative'`; }
	}
}
export namespace Cyes__has_alternative {
	export class Dtail extends Cview_expression_list {
		constructor(data:Tyes__has_alternative['tail'], parent:Cyes__has_alternative) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node()).result!)
			}, 'tail')
		}
	}
}
export namespace Cmain {
	export class Dinitial_content extends AlanDictionary<{ node:Cinitial_content, init:Tinitial_content},Cmain> {
		protected initialize(parent:Cmain, key:string, entry_init:Tinitial_content) { return new Cinitial_content(key, entry_init, parent); }
		protected finalize = finalize_initial_content
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'initial content'`; }
		constructor(data:Tmain['initial content'], parent:Cmain) {
			super(data, parent);
		}
	}
	export class Droot_view extends Reference<views.Cviews__,string> {
		public readonly inferences:{
			constraint: () => gui_model.Cgui_node
		}

		constructor(data:string, $this:Cmain) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.views?.ref)
				.then(context => {
					const entry = context?.properties.views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				constraint: cache((detach:boolean) => {
					const main__root_view_nval = $this.properties.root_view.ref;
					return resolve($this.properties.root_view.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => main__root_view_nval)
								.then(context => context?.properties.node_type)
								.then(context => context?.component_root.output.result_gui_node()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.root.input.gui_model)
								.then(context => context?.properties.root).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'root view'`; }
	}
	export class Droot_window extends Reference<settings.Cwindows,string> {

		constructor(data:string, $this:Cmain) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.settings)
				.then(context => {
					const entry = context?.properties.windows.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'root window'`; }
	}
	export class Dviews extends Reference<views.Cviews,string> {

		constructor(data:string, $this:Cmain) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this.root.input.views[this.entry]).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'views'`; }
	}
}
export namespace Cinitial_content {
	export class Dexpression extends Cview_expression {
		constructor(data:Tinitial_content['expression'], parent:Cinitial_content) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.from?.state.node.output.node()).result!)
			}, 'expression')
		}
	}
	export class Dfrom extends StateGroup<
		{ name: 'root', node:Croot__from, init:Troot__from}|
		{ name: 'user', node:Cuser__from, init:Tuser__from}> {
		protected initializer(state: 'root'|'user') {
			switch (state) {
				case 'root': return (init:Troot__from, parent:Cinitial_content) => new Croot__from(init, parent);
				case 'user': return (init:Tuser__from, parent:Cinitial_content) => new Cuser__from(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'root'|'user') {
			switch (state) {
				case 'root': return finalize_root__from;
				case 'user': return finalize_user__from;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tinitial_content['from'], parent:Cinitial_content) {
			super(data, parent, 'from');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'from'`; }
	}
}
function finalize_no__has_step(obj:Cno__has_step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
}
function finalize_yes__has_step(obj:Cyes__has_step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.parent)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
	finalize_ancestor_node_path(obj.properties.tail, detach);
}
function finalize_ancestor_node_path(obj:Cancestor_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
	switch (obj.properties.has_step.state.name) {
		case 'no': finalize_no__has_step(obj.properties.has_step.state.node, detach); break;
		case 'yes': finalize_yes__has_step(obj.properties.has_step.state.node, detach); break;
	}
}
function finalize_node_equality(obj:Cnode_equality, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
}
function finalize_branches(obj:Cbranches, detach:boolean = false) {
	finalize_view_expression_list(obj.properties.list, detach);
}
function finalize_collection_lookup(obj:Ccollection_lookup, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cyes__has_user_node>obj.inferences.base)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__navigable>obj.properties.collection.inferences.key_navigable)(detach) !== undefined || detach);
	finalize_view_expression(obj.properties.expression, detach);
	finalize_node_equality(obj.properties.references_users, detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.properties.references_users.inferences.constraint)(detach) !== undefined || detach);
}
function finalize_group_step(obj:Cgroup_step, detach:boolean = false) {
	finalize_view_expression(obj.properties.expression, detach);
	assert((<(detach?:boolean) => gui_model.Cgroup__type__property>(obj.properties.group as any).resolve)(detach) !== undefined || detach);
}
function finalize_none(obj:Cnone, detach:boolean = false) {
}
function finalize_context_rule(obj:Ccontext_rule, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>obj.inferences.state)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
}
function finalize_property_rule(obj:Cproperty_rule, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__navigable>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
}
function finalize_reference(obj:Creference, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__navigable>(obj.properties.reference as any).resolve)(detach) !== undefined || detach);
}
function finalize_reference_step(obj:Creference_step, detach:boolean = false) {
	finalize_view_expression(obj.properties.expression, detach);
	switch (obj.properties.type.state.name) {
		case 'context rule': finalize_context_rule(obj.properties.type.state.node, detach); break;
		case 'property rule': finalize_property_rule(obj.properties.type.state.node, detach); break;
		case 'reference': finalize_reference(obj.properties.type.state.node, detach); break;
	}
}
function finalize_cases(obj:Ccases, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_view_expression(obj.properties.expression, detach);
	finalize_ancestor_node_path(obj.properties.path, detach);
}
function finalize_state_switch(obj:Cstate_switch, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.cases) {
		finalize_cases(entry, detach);
	}
	if (!detach) {
		(obj.properties.cases as any).eval_required_keys(detach);
	}
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>(obj.properties.stategroup as any).resolve)(detach) !== undefined || detach);
}
function finalize_current(obj:Ccurrent, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
	finalize_ancestor_node_path(obj.properties.path, detach);
}
function finalize_root__node(obj:Croot__node, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
}
function finalize_user__node(obj:Cuser__node, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cyes__has_user_node>obj.inferences.base)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
}
function finalize_view_open(obj:Cview_open, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cviews>(obj.properties.from as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.node.state.name) {
		case 'current': finalize_current(obj.properties.node.state.node, detach); break;
		case 'root': finalize_root__node(obj.properties.node.state.node, detach); break;
		case 'user': finalize_user__node(obj.properties.node.state.node, detach); break;
	}
	assert((<(detach?:boolean) => views.Cviews__>(obj.properties.view as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.properties.view.inferences.constraint)(detach) !== undefined || detach);
}
function finalize_view_expression(obj:Cview_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.node)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'branches': finalize_branches(obj.properties.type.state.node, detach); break;
		case 'collection lookup': finalize_collection_lookup(obj.properties.type.state.node, detach); break;
		case 'group step': finalize_group_step(obj.properties.type.state.node, detach); break;
		case 'none': finalize_none(obj.properties.type.state.node, detach); break;
		case 'reference step': finalize_reference_step(obj.properties.type.state.node, detach); break;
		case 'state switch': finalize_state_switch(obj.properties.type.state.node, detach); break;
		case 'view open': finalize_view_open(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__has_alternative(obj:Cno__has_alternative, detach:boolean = false) {
}
function finalize_yes__has_alternative(obj:Cyes__has_alternative, detach:boolean = false) {
	finalize_view_expression_list(obj.properties.tail, detach);
}
function finalize_view_expression_list(obj:Cview_expression_list, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.node)(detach) !== undefined || detach);
	finalize_view_expression(obj.properties.expression, detach);
	switch (obj.properties.has_alternative.state.name) {
		case 'no': finalize_no__has_alternative(obj.properties.has_alternative.state.node, detach); break;
		case 'yes': finalize_yes__has_alternative(obj.properties.has_alternative.state.node, detach); break;
	}
}
function finalize_root__from(obj:Croot__from, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
}
function finalize_user__from(obj:Cuser__from, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cyes__has_user_node>obj.inferences.base)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
}
function finalize_initial_content(obj:Cinitial_content, detach:boolean = false) {
	assert((<(detach?:boolean) => settings.Cwindows>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_view_expression(obj.properties.expression, detach);
	switch (obj.properties.from.state.name) {
		case 'root': finalize_root__from(obj.properties.from.state.node, detach); break;
		case 'user': finalize_user__from(obj.properties.from.state.node, detach); break;
	}
}
function finalize_main(obj:Cmain, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.initial_content) {
		finalize_initial_content(entry, detach);
	}
	if (!detach) {
	}
	assert((<(detach?:boolean) => views.Cviews__>(obj.properties.root_view as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.properties.root_view.inferences.constraint)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => settings.Cwindows>(obj.properties.root_window as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cviews>(obj.properties.views as any).resolve)(detach) !== undefined || detach);
}

export namespace Cmain {
	export function create(init:Tmain, input: {
	'gui_model':gui_model.Cgui_model
	'settings':settings.Csettings
	'views':{[name:string]:views.Cviews}}, symbols_file?: SymbolsFile):Cmain {
		const instance = new Cmain(init, input as any, symbols_file);
		return instance;
	};
}
